/**
 * Trainingstage: feste Wochentage mit Uhrzeit, einzelne Termine verschoben
 * oder abgesagt, der Plan-Tag rotiert.
 *
 * Eine Regel, zwei Leser: Die App zeigt damit die kommenden Termine, der
 * Scheduler legt damit die Erinnerung „Losgehen um …" an. Wer das zweimal
 * rechnet, lässt das Telefon zu einem Termin klingeln, den die App abgesagt
 * zeigt.
 */

import { stableId } from './ids.js';
import { addDays, wallTimeToUtc } from './time.js';
import type { IsoDate, WallTime } from './time.js';

/** Ein wiederkehrender Trainingstermin: Wochentag (ISO, 1 = Montag) und Uhrzeit. */
export interface TrainingSlot {
  id: string;
  weekday: number;
  time: string;
  durationMin: number;
  active: boolean;
  /**
   * Seit wann es die Regel gibt — `created_at` der Zeile (setzt der Server
   * beim ersten Abgleich; das Gerät bekommt sie mit). Fehlt sie (noch nicht
   * abgeglichen), gilt `updatedAt`: später als die Entstehung, nie früher.
   * Ohne beides keine Untergrenze.
   */
  createdAt?: string | Date | null | undefined;
  updatedAt?: string | Date | null | undefined;
}

/**
 * Ab wann eine Wochenregel Termine hat (Geräteabnahme 28.10.2026): Ein am
 * Mittwoch angelegter Donnerstag stand im KI-Coach zwei Wochen rückwirkend als
 * „verpasst“. Ein Termin zählt, wenn er nach dem Anlegen **endet** — wer die
 * Regel während des Trainings anlegt, hat den heutigen nicht verpasst.
 */
export function slotExistsSince(slot: TrainingSlot): Date | null {
  const stamp = slot.createdAt ?? slot.updatedAt ?? null;
  if (stamp === null) return null;
  const instant = stamp instanceof Date ? stamp : new Date(stamp);
  return Number.isNaN(instant.getTime()) ? null : instant;
}

/**
 * Ein einzelner Tag, der von der Regel abweicht oder dazukommt.
 *
 * Mit `slotId` + `originDate` ersetzt er genau einen Termin der Regel
 * (verschoben oder abgesagt); ohne ist er ein zusätzlicher Termin.
 */
export interface TrainingDay {
  id: string;
  slotId: string | null;
  originDate: string | null;
  date: string;
  time: string;
  durationMin: number;
  status: 'planned' | 'cancelled';
}

export interface TrainingOccurrence {
  /** Kennung des Tages, falls schon als Zeile vorhanden. */
  dayId: string | null;
  slotId: string | null;
  /** Der Termin der Regel, den dieser ersetzt — Schlüssel fürs Verschieben. */
  originDate: string | null;
  date: string;
  time: string;
  durationMin: number;
  status: 'planned' | 'cancelled';
}

/** Wochentag eines Kalendertags (ISO, 1 = Montag) — ohne Zeitzone, der Tag ist schon lokal. */
export function weekdayOf(date: string): number {
  const day = new Date(`${date}T12:00:00Z`).getUTCDay();
  return day === 0 ? 7 : day;
}

/**
 * Alle Termine von `from` bis `to` (beide eingeschlossen), nach Datum und Zeit.
 *
 * Abgesagte bleiben in der Liste (mit Status) — die App zeigt sie
 * durchgestrichen, damit man sieht, dass da etwas war. Verschobene erscheinen an
 * ihrem neuen Tag, auch wenn der außerhalb des Fensters des alten liegt.
 *
 * Eine Wochenregel hat erst Termine, seit es sie gibt (`slotExistsSince`) —
 * sonst zählten Coach, Rückblick und Erfolge Wochen vor dem Anlegen als
 * geplant und verpasst. Die Zeitzone braucht es genau dafür.
 */
export function trainingOccurrences(
  slots: readonly TrainingSlot[],
  days: readonly TrainingDay[],
  from: string,
  to: string,
  timeZone: string,
): TrainingOccurrence[] {
  const replaced = new Map<string, TrainingDay>();
  for (const day of days) {
    if (day.slotId && day.originDate) replaced.set(`${day.slotId}|${day.originDate}`, day);
  }

  const since = new Map(slots.map((slot) => [slot.id, slotExistsSince(slot)]));
  const result: TrainingOccurrence[] = [];
  for (let date = from; date <= to; date = addDays(date as IsoDate, 1)) {
    const weekday = weekdayOf(date);
    for (const slot of slots) {
      if (!slot.active || slot.weekday !== weekday) continue;
      if (replaced.has(`${slot.id}|${date}`)) continue;
      const existsSince = since.get(slot.id);
      if (existsSince) {
        const end =
          occurrenceStart({ date, time: slot.time }, timeZone).getTime() +
          slot.durationMin * 60_000;
        if (end <= existsSince.getTime()) continue;
      }
      result.push({
        dayId: null,
        slotId: slot.id,
        originDate: date,
        date,
        time: slot.time,
        durationMin: slot.durationMin,
        status: 'planned',
      });
    }
  }
  // Ausnahmen einer Regel, die es nicht mehr gibt, gelten nicht mehr: Sonst
  // bliebe ein verschobener Termin einer gelöschten Wochenregel als Training
  // stehen — mit Erinnerung, Abo-Eintrag und Bilanz (Abnahme 13.10.2026).
  const known = new Set(slots.map((slot) => slot.id));
  for (const day of days) {
    if (day.slotId && !known.has(day.slotId)) continue;
    if (day.date < from || day.date > to) continue;
    result.push({
      dayId: day.id,
      slotId: day.slotId,
      originDate: day.originDate,
      date: day.date,
      time: day.time,
      durationMin: day.durationMin,
      status: day.status,
    });
  }
  return result.sort((a, b) => a.date.localeCompare(b.date) || a.time.localeCompare(b.time));
}

/**
 * Plan-Tage der Reihe nach auf die kommenden Termine verteilen.
 *
 * Rotation statt fester Zuordnung (Entscheidung 13.10.2026): Der nächste
 * Termin bekommt den Plan-Tag nach dem zuletzt trainierten. Fällt einer aus,
 * verschiebt sich nichts — der ausgefallene Plan-Tag kommt beim nächsten Mal.
 * Abgesagte Termine bekommen keinen.
 */
export function rotatePlanDays<T extends { status: 'planned' | 'cancelled' }>(
  occurrences: readonly T[],
  planDayIds: readonly string[],
  lastPlanDayId: string | null,
): (T & { planDayId: string | null })[] {
  if (planDayIds.length === 0) return occurrences.map((entry) => ({ ...entry, planDayId: null }));
  const last = lastPlanDayId ? planDayIds.indexOf(lastPlanDayId) : -1;
  let next = (last + 1) % planDayIds.length;
  return occurrences.map((entry) => {
    if (entry.status === 'cancelled') return { ...entry, planDayId: null };
    const planDayId = planDayIds[next]!;
    next = (next + 1) % planDayIds.length;
    return { ...entry, planDayId };
  });
}

/** So lange nach dem Ende ohne Einheit gilt ein Termin als verpasst. */
export const MISSED_AFTER_MIN = 60;

/**
 * Verpasst (18.10.2026): geplant, an dem Tag keine Einheit, und eine Stunde
 * nach dem Ende ist vorbei. Abgesagtes ist nie verpasst — das war eine
 * Entscheidung.
 */
export function isMissed(
  occurrence: { date: string; time: string; durationMin: number; status: 'planned' | 'cancelled' },
  trained: boolean,
  now: Date,
  timeZone: string,
): boolean {
  if (occurrence.status !== 'planned' || trained) return false;
  const end =
    occurrenceStart(occurrence, timeZone).getTime() +
    (occurrence.durationMin + MISSED_AFTER_MIN) * 60_000;
  return now.getTime() > end;
}

/**
 * Plan-Tage verteilen, wie App und Scheduler sie zeigen (18.10.2026).
 *
 * Erledigte (Einheit am Tag) und verpasste Termine verbrauchen **keinen**
 * Plan-Tag: Der verpasste Tag wartet aufs nächste Training. Vorher bekam ein
 * verpasster Termin von heute seinen Plan-Tag weiter — am Abend stand beim
 * nächsten Termin schon der übernächste, und im Scheduler zählte zusätzlich
 * eine Einheit ohne Plan-Tag als „zuletzt trainiert“.
 */
export function assignPlanDays<T extends TrainingOccurrence>(
  occurrences: readonly T[],
  input: {
    planDayIds: readonly string[];
    /** Plan-Tag der letzten Einheit **mit** Plan-Tag. */
    lastPlanDayId: string | null;
    trainedDates: ReadonlySet<string>;
    now: Date;
    timeZone: string;
  },
): (T & { planDayId: string | null; missed: boolean; done: boolean })[] {
  const flagged = occurrences.map((entry) => {
    const done = entry.status === 'planned' && input.trainedDates.has(entry.date);
    const missed = isMissed(entry, done, input.now, input.timeZone);
    return { entry, done, missed };
  });
  const open = flagged.filter((item) => !item.done && !item.missed).map((item) => item.entry);
  const rotated = rotatePlanDays(open, input.planDayIds, input.lastPlanDayId);
  const planDayOf = new Map(open.map((entry, index) => [entry, rotated[index]!.planDayId]));
  return flagged.map(({ entry, done, missed }) => ({
    ...entry,
    planDayId: planDayOf.get(entry) ?? null,
    missed,
    done,
  }));
}

/** Beginn eines Termins als Zeitpunkt. */
export function occurrenceStart(
  occurrence: { date: string; time: string },
  timeZone: string,
): Date {
  return wallTimeToUtc(occurrence.date as IsoDate, occurrence.time as WallTime, timeZone);
}

/**
 * Wann losgehen, wann erinnern: Losgehen = Beginn − Wegzeit, die Mitteilung
 * kommt noch den Vorlauf davor (`profiles.travel_min`/`lead_min`).
 */
export function departure(
  start: Date,
  travelMin: number,
  leadMin: number,
): { leaveAt: Date; remindAt: Date } {
  const leaveAt = new Date(start.getTime() - Math.max(0, travelMin) * 60_000);
  const remindAt = new Date(leaveAt.getTime() - Math.max(0, leadMin) * 60_000);
  return { leaveAt, remindAt };
}

/** Kennung eines Termins der Wochenregel — dieselbe in App und Scheduler. */
export function occurrenceId(slotId: string, originDate: string): string {
  return stableId(`training-day|${slotId}|${originDate}`);
}
